import classNames from 'classnames';
import chunk from 'lodash/chunk';
import sumBy from 'lodash/sumBy';
import * as React from 'react';
import type { SearchResultResponse } from '../../Search/SearchResultResponse.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import { HtmlHeading } from '~/Partials/Controls/HtmlHeading';
import { PagedFetchData } from '~/Partials/PagedFetchData/PagedFetchData';
import { Picture } from '~/Partials/Picture/Picture';
import { PicturePlaceholder } from '~/Partials/Picture/Placeholder/PicturePlaceholder';
import type { PosterViewModel } from './Poster/PosterViewModel.csharp';
import styles from './PosterListBlock.module.css';
import type { PosterListBlockViewModel } from './PosterListBlockViewModel.csharp';
import { PosterListRow } from './PosterListRow/PosterListRow';

type PosterListBlockProps = PosterListBlockViewModel & { className?: string };

export const PosterListBlock: React.FC<React.PropsWithChildren<PosterListBlockProps>> = props => {
    const {
        posters,
        displayRows,
        backgroundVariant,
        title,
        sectionBlockId,
        loadMorePostersUrl,
        loadOnScroll,
        translations,
        postersCategory,
        loadedRowLayout,
        loadedPostersPictureProfile,
        className,
    } = props;

    const postersCopy = [...posters];

    const rowsWithData = displayRows.map(row => ({ ...row, posters: postersCopy.splice(0, row.itemsCount) }));
    const skip = sumBy(rowsWithData, row => row.itemsCount);

    const getSearchParams = () => {
        const params = [{ name: 'sectionBlockId', value: sectionBlockId }];

        postersCategory && params.push({ name: 'categoryName', value: postersCategory });

        return params;
    };

    return (
        <>
            {!!rowsWithData.length && rowsWithData.every(r => !!r.posters && !!r.posters.length) && (
                <section
                    className={classNames(styles.PosterListBlock, styles[`Variant--${backgroundVariant}`], className)}
                >
                    <div className={styles.Content}>
                        <div id={sectionBlockId} className={styles.Anchor} />
                        <h2 className={styles.Title}>{title}</h2>
                        <div className={styles.Inner}>
                            {rowsWithData.map((row, index) => (
                                <PosterListRow key={`${sectionBlockId}_${index}`} {...row} />
                            ))}
                        </div>
                        {skip > 0 && (
                            <div className={styles.LoadedData}>
                                <PagedFetchData<SearchResultResponse<PosterViewModel>, PosterViewModel>
                                    baseUrl={loadMorePostersUrl}
                                    debounceTime={200}
                                    take={loadedRowLayout ? loadedRowLayout.itemsCount : 8}
                                    initSkip={skip}
                                    autoRun={false}
                                    loadOnScroll={loadOnScroll}
                                    translations={translations}
                                    searchParams={getSearchParams()}
                                    allDataRender={
                                        !!loadedRowLayout &&
                                        (({ data }) => {
                                            const dividedData = chunk(data, loadedRowLayout.itemsCount);

                                            return (
                                                <div className={styles.AllData}>
                                                    {dividedData.map((row, index) => (
                                                        <PosterListRow
                                                            key={`AllDataRendered_${index}`}
                                                            posters={row}
                                                            {...loadedRowLayout}
                                                            reverse={index % 2 === 0}
                                                        />
                                                    ))}
                                                </div>
                                            );
                                        })
                                    }
                                    listClassName={styles.SingleData}
                                >
                                    {!loadedRowLayout
                                        ? ({ data }) => (
                                              <>
                                                  {data.image ? (
                                                      <Picture
                                                          {...data.image}
                                                          profile={loadedPostersPictureProfile}
                                                          className={styles.Picture}
                                                      />
                                                  ) : (
                                                      <PicturePlaceholder className={styles.Picture} />
                                                  )}
                                                  <div className={styles.LoadedDataText}>
                                                      <span>{data.category}</span>
                                                      {data.title && <HtmlHeading type="h3" html={data.title} />}
                                                  </div>
                                                  <a
                                                      href={data.primaryLinkUrl}
                                                      className="PosterListBlock__linkOverlay"
                                                  >
                                                      {data.title && (
                                                          <HtmlDiv className="visuallyHidden" html={data.title} />
                                                      )}
                                                  </a>
                                              </>
                                          )
                                        : undefined}
                                </PagedFetchData>
                            </div>
                        )}
                    </div>
                </section>
            )}
        </>
    );
};
